import {
  AgentRuleBundle,
  CustomDocSection,
  SourceTierExamples,
} from "../agent-config-options";
import { AgentRule } from "../types";

/**
 * Tier metadata used by {@link renderSourceTierExamples} to locate the
 * matching heading inside the base bundle's "Source Quality &
 * Verification" rule.
 */
const SOURCE_TIER_HEADINGS: ReadonlyArray<{
  readonly key: keyof SourceTierExamples;
  readonly heading: string;
}> = [
  { key: "t1", heading: "### T1 — Primary Living" },
  { key: "t2", heading: "### T2 — Primary Snapshot" },
  { key: "t3", heading: "### T3 — Secondary" },
  { key: "t4", heading: "### T4 — Self-Reported / Marketing" },
];

/**
 * Inject domain-specific source-tier examples into the content of the
 * base bundle's "Source Quality & Verification" rule.
 *
 * For every tier whose examples array is non-empty, a
 * `**Project-specific examples:**` line followed by a bullet list of
 * the supplied strings is appended immediately beneath the tier's
 * `### T<n> — ...` heading paragraph block. Tiers whose examples array
 * is missing or empty are left untouched.
 *
 * Returns the original `content` verbatim when the supplied config is
 * undefined or has no non-empty tier arrays, so callers can
 * unconditionally pipe content through this function.
 */
export function renderSourceTierExamples(
  content: string,
  examples: SourceTierExamples | undefined,
): string {
  if (!examples) {
    return content;
  }

  const tierEntries = SOURCE_TIER_HEADINGS.flatMap(({ key, heading }) => {
    const list = examples[key];
    if (!list || list.length === 0) {
      return [];
    }
    return [{ heading, list }];
  });

  if (tierEntries.length === 0) {
    return content;
  }

  let updated = content;
  for (const { heading, list } of tierEntries) {
    const injection = [
      "",
      "**Project-specific examples:**",
      "",
      ...list.map((ex) => `- ${ex}`),
    ].join("\n");
    updated = insertAfterHeadingBlock(updated, heading, injection);
  }

  return updated;
}

/**
 * Apply every {@link CustomDocSection} that targets `bundle.name` to
 * the bundle's rules, returning a new bundle whose matched rules have
 * the section bodies appended after the configured `afterSection`
 * heading. Entries that reference a bundle name other than
 * `bundle.name`, or whose `afterSection` cannot be located in any
 * rule in the bundle, are silently dropped.
 *
 * Entries that target the same `afterSection` heading render in
 * supplied order — the first supplied entry appears immediately
 * beneath the target heading block, the second below that, and so on.
 * This is achieved by advancing the insertion anchor to each
 * just-injected section's `## <sectionTitle>` heading; the next
 * same-target entry then lands at the end of that newly-opened
 * section, which places it immediately after the previous entry
 * regardless of the original heading's level.
 *
 * Returns `bundle` unchanged when `sections` is empty or no entries
 * match, so callers can unconditionally pipe bundles through this
 * function without penalty.
 */
export function renderCustomDocSections(
  bundle: AgentRuleBundle,
  sections: ReadonlyArray<CustomDocSection>,
): AgentRuleBundle {
  const matching = sections.filter((s) => s.bundleName === bundle.name);
  if (matching.length === 0) {
    return bundle;
  }

  let rules: ReadonlyArray<AgentRule> = bundle.rules;
  // Per-target (afterSection -> current anchor heading line) map so
  // successive entries targeting the same heading chain onto the
  // previous injection instead of landing in reverse order.
  const currentAnchorByTarget = new Map<string, string>();

  for (const section of matching) {
    const injection = renderCustomDocSectionBlock(section);

    // Pick the anchor heading to insert after: either the previous
    // injection's own heading (for subsequent entries on the same
    // target) or the user-supplied `afterSection` heading.
    const seededAnchor = currentAnchorByTarget.get(section.afterSection);

    let targetIndex = -1;
    let targetHeading = "";
    for (let i = 0; i < rules.length; i += 1) {
      if (seededAnchor && rules[i].content.includes(seededAnchor)) {
        targetIndex = i;
        targetHeading = seededAnchor;
        break;
      }
      if (!seededAnchor) {
        const match = findHeadingForTitle(
          rules[i].content,
          section.afterSection,
        );
        if (match) {
          targetIndex = i;
          targetHeading = match;
          break;
        }
      }
    }
    if (targetIndex === -1) {
      continue;
    }

    const targetRule = rules[targetIndex];
    const updatedRule: AgentRule = {
      ...targetRule,
      content: insertAfterHeadingBlock(
        targetRule.content,
        targetHeading,
        injection,
      ),
    };
    rules = [
      ...rules.slice(0, targetIndex),
      updatedRule,
      ...rules.slice(targetIndex + 1),
    ];

    // The newly-injected `## <sectionTitle>` heading becomes the anchor
    // the next same-target entry chains onto.
    currentAnchorByTarget.set(
      section.afterSection,
      `## ${section.sectionTitle}`,
    );
  }

  if (rules === bundle.rules) {
    return bundle;
  }

  return { ...bundle, rules };
}

/**
 * Render a {@link CustomDocSection} into the markdown block that
 * `renderCustomDocSections` splices after the target heading block.
 * Exposed for tests that exercise the renderer directly without
 * touching a full bundle.
 */
export function renderCustomDocSectionBlock(section: CustomDocSection): string {
  const body = section.body.replace(/[\r\n]+$/u, "");
  return ["", `## ${section.sectionTitle}`, "", body].join("\n");
}

/**
 * Return the first full markdown heading line in `content` whose text
 * (after the `# ` prefix) equals `title`, regardless of heading level
 * (`#` through `######`). Matching is line-anchored so a mention of
 * the title in prose never counts. Returns `undefined` when no heading
 * in the document has the supplied title.
 */
function findHeadingForTitle(
  content: string,
  title: string,
): string | undefined {
  const lines = content.split("\n");
  for (const line of lines) {
    const match = line.match(/^(#{1,6}) (.+)$/u);
    if (match && match[2] === title) {
      return line;
    }
  }
  return undefined;
}

/**
 * Insert `injection` into `content` immediately after the end of the
 * section opened by `heading`. The section runs from the heading
 * itself through every subsequent line up to (but not including) the
 * next heading at the same or shallower level. Trailing blank lines
 * on the closing of the section are preserved in place and the
 * injection is spliced in before them so markdown spacing stays
 * consistent.
 *
 * If `heading` is not found, returns `content` unchanged.
 */
function insertAfterHeadingBlock(
  content: string,
  heading: string,
  injection: string,
): string {
  const lines = content.split("\n");
  const headingIndex = lines.findIndex((line) => line === heading);
  if (headingIndex === -1) {
    return content;
  }

  const headingLevel = countLeadingHashes(heading);

  // Scan forward from the heading to locate the end of the section —
  // the line *before* the next heading at the same or shallower level,
  // or the end of the document when no such heading exists.
  let endIndex = lines.length - 1;
  for (let i = headingIndex + 1; i < lines.length; i += 1) {
    const nextLevel = countLeadingHashes(lines[i]);
    if (nextLevel > 0 && nextLevel <= headingLevel) {
      endIndex = i - 1;
      break;
    }
  }

  // Trim trailing blank lines off the section so the injection lands
  // directly at the end of the section's content, not after a string
  // of blank lines.
  while (endIndex > headingIndex && lines[endIndex] === "") {
    endIndex -= 1;
  }

  const injectionLines = injection.split("\n");
  const next = [
    ...lines.slice(0, endIndex + 1),
    ...injectionLines,
    ...lines.slice(endIndex + 1),
  ];
  return next.join("\n");
}

/**
 * Count the number of `#` characters at the start of a markdown
 * heading line. Returns `0` when the line is not a heading.
 */
function countLeadingHashes(line: string): number {
  const match = line.match(/^(#{1,6}) /u);
  return match ? match[1].length : 0;
}
